import { useMemo } from 'react';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { getReactionsSummary, getReactionsQueryInfo } from '../utils/data.ts';
import { getBooleanLocationTypes } from '../utils/reactions-utils.ts';
import type { ReactionsContentQuery_content_nodes_contentReactionsSummary_reactionsSummaryForEmoji as ReactionsSummaryForEmoji } from '../graphql/__types__/ReactionsContentQuery.ts';
import type {
	ReactionLocation,
	Reaction,
	ReactionsData,
	QueryData,
	QueryVariables,
	ReactedUsersMap,
} from '../reactionTypes.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
type ReactionsDataValue = {
	reactions: Reaction[];
	totalCount: number;
	ari: string;
	containerAri: string;
};

type ReactionsDataReturnType = {
	data: ReactionsDataValue;
	loading?: boolean;
	error?: ApolloErrorInterop;
};

type ReactionsDataFnProps = (input: {
	location: ReactionLocation;
	contentId: string;
	containerId: string;
	reactedUsersMap: ReactedUsersMap;
	useNetworkOnly: boolean;
	inheritedReactionsData?: ReactionsData;
	limit?: number;
	sort?: 'asc' | 'desc';
}) => ReactionsDataReturnType;

export const useReactionsData: ReactionsDataFnProps = ({
	location,
	contentId,
	containerId,
	reactedUsersMap,
	useNetworkOnly,
	inheritedReactionsData,
	limit,
	sort,
}) => {
	const { isComment } = getBooleanLocationTypes(location);
	const isLivePage = useIsCurrentPageLive();

	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	const isLivePageSSRMode = isLivePage && Boolean(process.env.REACT_SSR || window.__SSR_RENDERED__);

	const { query, queryVariables } = getReactionsQueryInfo(isComment, contentId, containerId);

	const { data, loading, error } = useQueryInterop<QueryData, QueryVariables>(query, {
		variables: queryVariables,
		fetchPolicy: isComment && useNetworkOnly ? 'network-only' : 'cache-first',
		skip: inheritedReactionsData !== undefined, // null is a valid case meaning an error ocurred higher up
	});

	return useMemo(() => {
		// Sometimes we want to show preloaded data during loading instead of empty state
		const showPreloadedDataDuringLoading =
			isLivePageSSRMode || // Always show preloaded data during SSR
			window.__SSR_REACT_STREAMING__;

		const shouldShowEmptyState =
			(loading && !showPreloadedDataDuringLoading) || // Show empty while loading unless we want to show cached data
			error ||
			(!data && !inheritedReactionsData);

		if (shouldShowEmptyState) {
			return {
				data: {
					reactions: [],
					totalCount: 0,
					ari: '',
					containerAri: '',
				},
				loading,
				error,
			};
		}

		const reactionsSummary = inheritedReactionsData || getReactionsSummary(data);

		const totalCount = reactionsSummary?.reactionsCount || 0;
		const containerAri = reactionsSummary?.containerAri || '';
		const ari = reactionsSummary?.ari || '';
		const reactionNodes = reactionsSummary?.reactionsSummaryForEmoji || [];

		// We need to filter out all the "null" reactions
		const filteredNodes = reactionNodes?.filter(
			(reaction) => reaction !== null,
		) as ReactionsSummaryForEmoji[];

		// Transform reaction nodes to the correct shape for the AkReactions component
		let shapedReactions = filteredNodes.map(({ emojiId, count, reacted }) => {
			const users = reactedUsersMap[emojiId] || [];

			return {
				ari,
				containerAri,
				emojiId,
				count,
				reacted,
				users,
			};
		});

		if (sort) {
			shapedReactions = shapedReactions.sort((a, b) =>
				sort === 'asc' ? a.count - b.count : b.count - a.count,
			);
		}

		if (limit) {
			shapedReactions = shapedReactions.slice(0, limit);
		}

		return {
			data: {
				totalCount,
				reactions: shapedReactions,
				ari,
				containerAri,
			},
		};
	}, [
		data,
		inheritedReactionsData,
		reactedUsersMap,
		loading,
		error,
		sort,
		limit,
		isLivePageSSRMode,
	]);
};
